import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, LogOut, Wallet } from "lucide-react";
import { api, formatR, ORDER_STATUS_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";
import { BackButton } from "@/components/BackButton";
import { StokvelWallet } from "@/components/StokvelWallet";

export default function AccountPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (user === false) navigate("/auth");
  }, [user, navigate]);

  useEffect(() => {
    if (user) api.get("/orders/my/list").then((r) => setOrders(r.data.orders)).catch(() => setOrders([]));
  }, [user]);

  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12" data-testid="account-page">
      <div className="mb-4">
        <BackButton to="/shop" label="Back to shop" testId="account-back-button" />
      </div>
      <Reveal>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-navy">Hi, {user.name}</h1>
            <p className="text-sm text-slate-500 mt-1">{user.email}</p>
          </div>
          <button
            onClick={async () => {
              await logout();
              navigate("/");
            }}
            className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-red-500 transition-colors"
            data-testid="logout-button"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </Reveal>

      <Reveal delay={0.05} className="mt-8">
        <h2 className="font-display text-lg font-bold text-navy flex items-center gap-2 mb-3" data-testid="account-stokvel-heading">
          <Wallet size={17} /> Stokvel Wallet
        </h2>
        <StokvelWallet showBanking brand="Stokvel Wallet" />
      </Reveal>

      <Reveal delay={0.1} className="mt-8">
        <h2 className="font-display text-lg font-bold text-navy flex items-center gap-2">
          <Package size={17} /> Order history
        </h2>
        {!orders ? (
          <div className="mt-4 bg-white rounded-2xl border border-slate-100 h-40 animate-pulse" />
        ) : orders.length === 0 ? (
          <div className="mt-4 bg-white rounded-3xl border border-slate-200/80 p-12 text-center" data-testid="no-orders">
            <p className="text-sm text-slate-500">No orders yet.</p>
            <Link to="/shop" className="text-brand font-bold text-sm mt-2 inline-block" data-testid="account-shop-link">
              Start shopping
            </Link>
          </div>
        ) : (
          <div className="mt-4 space-y-3" data-testid="orders-list">
            {orders.map((o) => (
              <Link
                key={o.id}
                to={`/order/${o.order_number}`}
                state={{ email: o.customer.email }}
                className="bg-white rounded-2xl border border-slate-200/80 p-5 flex items-center gap-4 hover:border-gold transition-colors flex-wrap"
                data-testid={`order-row-${o.order_number}`}
              >
                <div className="flex-1 min-w-[140px]">
                  <p className="font-mono font-bold text-navy text-sm">{o.order_number}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {new Date(o.created_at).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" })} · {o.items.length} item{o.items.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <span className={`text-[11px] font-bold px-3 py-1 rounded-full ${o.payment_status === "paid" ? "bg-emerald-50 text-emerald-600" : "bg-gold/15 text-gold-hover"}`}>
                  {ORDER_STATUS_LABELS[o.status] || o.status}
                </span>
                <span className="font-mono font-bold text-navy">{formatR(o.total)}</span>
              </Link>
            ))}
          </div>
        )}
      </Reveal>
    </div>
  );
}
